import React, { useEffect, useRef, useState } from "react";
import styles from "./CategoryCard.module.scss";
import CustomSelect from "@/commons/components/fields/customSelect/CustomSelect";
import FloatingInput from "@/commons/components/fields/floatingInput/FloatingInput";
import CustomButton from "@/commons/components/buttons/customButton/CustomButton";
import CustomizedTooltip from "@/commons/utils/toolpit/CustomizedTooltip";

interface CategoryCardProps {
    category: {
        id: string;
        name: string;
        usable: string;
        hidden: boolean;
        orderId: number;
        formattedForm: string;
    };
    usabilities: string[];
    onEdit: (category: {
        id: string;
        name: string;
        usable: string;
        hidden: boolean;
        orderId: number;
        formattedForm: string;
    }) => void;
    onDelete: (id: string) => void;
    onToggleStatus: (
        category: {
            id: string;
            name: string;
            usable: string;
            hidden: boolean;
            orderId: number;
            formattedForm: string;
        },
        newStatus: boolean
    ) => void;
    onChangeOrder: (catId: string, value: number) => void;
    onChangeUsability: (value: string) => void;
}

const CategoryCard: React.FC<CategoryCardProps> = ({
                                                       category,
                                                       usabilities,
                                                       onEdit,
                                                       onDelete,
                                                       onToggleStatus,
                                                       onChangeOrder,
                                                       onChangeUsability,
                                                   }) => {
    const isDisabled = category.hidden;
    const isSeparator = category.usable.toLowerCase() === "separator";
    const isDeleted = category.formattedForm === "deleted";

    const [orderValue, setOrderValue] = useState<string>(String(category.orderId));
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        // Quando l’utente smette di digitare per 1 secondo, invia update
        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        timeoutRef.current = setTimeout(() => {
            const parsed = Number(orderValue);
            if (!isNaN(parsed) && parsed !== category.orderId) {
                onChangeOrder(category.id, parsed);
            }
        }, 1000);

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [orderValue]);

    useEffect(() => {
        setOrderValue(String(category.orderId));
    }, [category.orderId]);

    if (isDeleted) {
        return (
            <div className={`${styles.categoryBox} ${styles.categoryDeleted}`}>
                <div className={styles.categoryName}>
                    [{category.id}] {category.name}
                </div>
                <CustomButton
                    onClick={() =>
                        onEdit({
                            ...category,
                            formattedForm: JSON.stringify([]),
                        })
                    }
                >
                    Riattiva
                </CustomButton>
            </div>
        );
    }

    return (
        <div
            className={`${styles.categoryBox} ${isDisabled ? styles.categoryDisabled : styles.categoryEnabled} ${isSeparator ? styles.categoryBoxSeparator : ""}`}
        >
            <div className={styles.categoryName} title={`[${category.id}] ${category.name}`}>
                <CustomizedTooltip tooltipContent={category.name}>
                    [{category.id}] {category.name}
                </CustomizedTooltip>
            </div>

            <div className={styles.categoryUsab}>
                <CustomSelect
                    label="Usabilità"
                    value={category.usable}
                    options={usabilities.map((u) => ({ label: u, value: u }))}
                    onChange={(value) => onChangeUsability(value)}
                />
            </div>

            <div className={styles.categoryOrder}>
                <FloatingInput
                    type="number"
                    label="Ordine"
                    id={`order-${category.id}`}
                    value={orderValue}
                    onChange={(e) => setOrderValue(e.target.value)}
                />
            </div>

            <CustomButton onClick={() => onEdit(category)}>Modifica</CustomButton>

            {!isDisabled ? (
                <CustomButton onClick={() => onToggleStatus(category, false)}>Disabilita</CustomButton>
            ) : (
                <>
                    <CustomButton onClick={() => onToggleStatus(category, true)}>Attiva</CustomButton>
                    <CustomButton onClick={() => onDelete(category.id)}>Elimina</CustomButton>
                </>
            )}
        </div>
    );
};

export default CategoryCard;
